import React, { useRef, useState } from 'react';
import { Video, Trash2, Upload } from 'lucide-react';
import api, { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { uploadVideo, VIDEO_MAX_MB } from '../lib/videoUpload';

const mb = (n) => `${(n / 1048576).toFixed(1)} MB`;

export const VideoBlock = ({ targetType, targetId, videos = [], canEdit = true, onChanged, max = 3 }) => {
  const inputRef = useRef(null);
  const [pct, setPct] = useState(null);

  const send = async (file) => {
    if (!file) return;
    if (file.size > VIDEO_MAX_MB * 1024 * 1024) {
      toast({ title: 'Vídeo muito grande', description: `Limite de ${VIDEO_MAX_MB}MB. Grave um trecho mais curto.` });
      return;
    }
    setPct(0);
    try {
      await uploadVideo(api, { targetType, targetId, file, onProgress: setPct });
      toast({ title: 'Vídeo enviado', description: file.name });
      onChanged && onChanged();
    } catch (e) {
      toast({ title: 'Falha no envio do vídeo', description: e.response?.data?.detail || 'Conexão interrompida. Tente novamente.' });
    } finally { setPct(null); }
  };

  const remove = async (v) => {
    try {
      await api.delete(`/media/video/${targetType}/${targetId}/${v.id}`);
      toast({ title: 'Vídeo removido' });
      onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro ao remover', description: e.response?.data?.detail || '' }); }
  };

  return (
    <div className="space-y-2.5" data-testid={`video-block-${targetType}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="ent-label flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> Vídeo do veículo ({videos.length}/{max})</p>
        {canEdit && (
          <button data-testid="video-add-btn" disabled={pct != null || videos.length >= max}
            onClick={() => inputRef.current?.click()}
            className="ent-btn ent-btn-sm text-primary border-primary/50 disabled:opacity-50">
            <Upload className="w-3 h-3" /> {pct != null ? `Enviando ${pct}%` : 'Enviar vídeo'}
          </button>
        )}
        <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/*" className="hidden"
          data-testid="video-file-input"
          onChange={(e) => { send(e.target.files?.[0]); e.target.value = ''; }} />
      </div>
      {pct != null && (
        <div className="h-1.5 rounded-full bg-muted overflow-hidden" data-testid="video-upload-progress">
          <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${pct}%` }} />
        </div>
      )}
      {videos.length === 0 ? (
        <p className="text-[11px] t-label">Nenhum vídeo. Grave um giro pelo carro ou o motor rodando (máx {VIDEO_MAX_MB}MB).</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="video-list">
          {videos.map((v) => (
            <div key={v.id} className="rounded-md border border-border overflow-hidden bg-black/40">
              <video data-testid={`video-player-${v.id}`} controls preload="metadata" playsInline
                src={`${API}/media/video/${v.path}`} className="w-full aspect-video bg-black" />
              <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                <p className="text-[10.5px] t-label truncate">{v.original_filename || 'vídeo'} · {mb(v.size || 0)} · {v.uploaded_by}</p>
                {canEdit && (
                  <button data-testid={`video-del-${v.id}`} onClick={() => remove(v)} title="Remover vídeo"
                    className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
